import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLang } from "@/i18n/LanguageContext";
import { PageShell } from "@/components/PageShell";
import { ConsultationModeBadge } from "@/components/ConsultationModeBadge";
import { Loader2, CheckCircle2, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LocalStorage key for in-progress Bilan drafts.
// Cleared on successful submit (Stripe redirect OR master bypass).
const DRAFT_STORAGE_KEY = "facow_bilan_draft_v1";

// ============================================================================
// UI helpers (kept visually identical to the previous Bilan version)
// ============================================================================

const Card = ({ title, num, subtitle, kind = "pilier", children, testId }) => (
  <div className="glass glow-border rounded-2xl p-6 md:p-7" data-testid={testId}>
    {num && (
      <p className="label-overline mb-2">
        {kind === "etape" ? `ÉTAPE ${num}` : `PILIER ${num}`}
      </p>
    )}
    <h3 className="font-display text-lg md:text-xl text-amber-glow mb-1">{title}</h3>
    {subtitle && <p className="text-xs text-slate-500 italic mb-4">{subtitle}</p>}
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="label-overline block mb-2">{label}</label>
    {hint && <p className="text-[11px] text-slate-500 italic mb-2 -mt-1">{hint}</p>}
    {children}
  </div>
);

// Reusable numeric input with sanitization (spaces stripped, comma → dot),
// custom error UI (red border + persistent message), and mobile-friendly
// inputMode. Replaces native `type="number"` which produced unfriendly
// browser pop-ups when users typed spaces or commas.
const NumericInput = ({
  value, onChange, integer = false, min, max,
  error, setError, placeholder, testId, isFr = true, required = false,
}) => {
  const sanitize = (raw) => {
    let s = String(raw ?? "").replace(/\s+/g, "").replace(",", ".");
    if (integer) {
      s = s.replace(/[^\d]/g, "");
    } else {
      s = s.replace(/[^\d.]/g, "");
      const parts = s.split(".");
      if (parts.length > 2) s = parts[0] + "." + parts.slice(1).join("");
    }
    return s;
  };
  const validRe = integer ? /^\d+$/ : /^\d+(\.\d+)?$/;

  const handleChange = (e) => {
    const cleaned = sanitize(e.target.value);
    onChange(cleaned);
    // Live-clear the error as soon as the value becomes valid
    if (cleaned === "" || validRe.test(cleaned)) {
      const n = cleaned === "" ? null : parseFloat(cleaned);
      const minOk = min === undefined || n === null || n >= min;
      const maxOk = max === undefined || n === null || n <= max;
      if (minOk && maxOk) setError("");
    }
  };

  const handleBlur = () => {
    if (value === "" || value === null || value === undefined) {
      if (required) setError(isFr ? "Champ requis." : "Required field.");
      else setError("");
      return;
    }
    if (!validRe.test(value)) {
      setError(integer
        ? (isFr ? "Veuillez n'entrer que des chiffres. Ex : 75" : "Please enter digits only. E.g. 75")
        : (isFr ? "Veuillez n'entrer qu'un nombre. Ex : 75 ou 75.5" : "Please enter a valid number. E.g. 75 or 75.5"));
      return;
    }
    const n = parseFloat(value);
    if (min !== undefined && n < min) {
      setError(isFr ? `Valeur minimale : ${min}` : `Minimum: ${min}`);
      return;
    }
    if (max !== undefined && n > max) {
      setError(isFr ? `Valeur maximale : ${max}` : `Maximum: ${max}`);
      return;
    }
    setError("");
  };

  return (
    <>
      <input
        type="text"
        inputMode={integer ? "numeric" : "decimal"}
        autoComplete="off"
        className={`input-tech ${error ? "!border-red-500 focus:!border-red-500 focus:!ring-red-500/40" : ""}`}
        value={value ?? ""}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        data-testid={testId}
        aria-invalid={!!error}
        aria-describedby={error ? `${testId}-error` : undefined}
      />
      {error && (
        <p id={`${testId}-error`} className="mt-1.5 text-xs text-red-400" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </>
  );
};

const Radio = ({ name, value, current, onChange, children, testId }) => (
  <label
    className={`cursor-pointer rounded-lg px-3 py-2 text-sm transition border flex-1 min-w-[140px] ${
      current === value
        ? "bg-[#00F0FF]/10 border-[#00F0FF]/55 text-slate-100"
        : "bg-[#0A1128]/60 border-[#1E293B] text-slate-300 hover:border-[#00F0FF]/30"
    }`}
    data-testid={testId}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={current === value}
      onChange={() => onChange(value)}
      className="hidden"
    />
    {children}
  </label>
);

const Check = ({ checked, onChange, children, testId }) => (
  <label
    className={`cursor-pointer rounded-lg px-3 py-2 text-sm transition border ${
      checked
        ? "bg-[#D4AF37]/10 border-[#D4AF37]/55 text-slate-100"
        : "bg-[#0A1128]/60 border-[#1E293B] text-slate-300 hover:border-[#D4AF37]/30"
    }`}
    data-testid={testId}
  >
    <input type="checkbox" checked={checked} onChange={onChange} className="hidden" />
    {children}
  </label>
);

// 1→10 scale renderer
const ScaleSelector = ({ value, onChange, testIdPrefix }) => (
  <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`rounded-lg px-3 py-2 text-sm font-mono-tech transition border ${
          value === n
            ? "bg-[#00F0FF]/15 border-[#00F0FF]/55 text-slate-100"
            : "bg-[#0A1128]/60 border-[#1E293B] text-slate-300 hover:border-[#00F0FF]/30"
        }`}
        data-testid={`${testIdPrefix}-${n}`}
      >
        {n}
      </button>
    ))}
  </div>
);

// ============================================================================
// Initial state — new 5-step questionnaire
// Fields are named in clean snake_case for direct LLM export.
// ============================================================================

const initialAnswers = {
  // Step 0 — Objective (multi-select)
  objective_tags: [],
  objective_other: "",

  // Step 1 — Rythme quotidien (Chronobiologie)
  wake_time: "",
  last_meal_time: "",
  sleep_hours_avg: "",
  night_wake_ups: "",
  daily_energy_level: null, // 1-10

  // Step 2 — Digestion & Bien-être (Naturopathie)
  digestion_score: null, // 1-10
  bloating_frequency: "", // souvent | parfois | rarement
  bloating_timing: "", // midi | soir
  stool_description: "",
  skin_hair_nails: "",
  medications_supplements: "",

  // Step 3 — Alimentation actuelle (Cuisine Vivante)
  meals_per_day: "",
  meat_fish_consumption: "",
  dairy_gluten_sugar: "",
  legumes: "",
  typical_breakfast: "",
  typical_lunch: "",
  typical_dinner: "",

  // Step 4 — Sensations corporelles (Ayurveda)
  food_temp_preference: "", // chauds | crus | both → dosha tag
  body_temperature: "", // frileux | chaud | normal → dosha tag
  dominant_emotion: "", // calme | irritable | anxieux | lent → dosha tag
  taste_preferences: [], // multi: salé / sucré / épicé / amer / acide

  // Step 5 — Chronobiologie, Jeûne & Sport
  peak_hunger_time: "",
  peak_energy_time: "",
  intermittent_fasting_window: "",
  physical_activity: "",
};

const OBJECTIVES = [
  { v: "more_energy", l_fr: "Plus d'énergie au quotidien", l_en: "More daily energy" },
  { v: "comfortable_digestion", l_fr: "Digestion plus confortable", l_en: "More comfortable digestion" },
  { v: "lighter_form", l_fr: "Se sentir plus léger et en forme", l_en: "Feel lighter and fitter" },
  { v: "better_sleep", l_fr: "Mieux dormir", l_en: "Better sleep" },
  { v: "weight_balance", l_fr: "Stabiliser ou prendre un peu de poids", l_en: "Stabilise or gain some weight" },
];

const TASTES = [
  { v: "salé", l_fr: "Salé", l_en: "Salty" },
  { v: "sucré", l_fr: "Sucré", l_en: "Sweet" },
  { v: "épicé", l_fr: "Épicé", l_en: "Spicy" },
  { v: "amer", l_fr: "Amer", l_en: "Bitter" },
  { v: "acide", l_fr: "Acide", l_en: "Sour" },
];

// ============================================================================
// Page
// ============================================================================

export default function Bilan() {
  const { locale, t } = useLang();
  const isFr = locale === "fr";

  const [engagement, setEngagement] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [identity, setIdentity] = useState({
    first_name: "",
    last_name: "",
    age: "",
    email: "",
    height_cm: "",
    weight_kg: "",
    blood_group: "",
    medical_followup: false,
    medical_followup_detail: "",
  });
  const [answers, setAnswers] = useState(initialAnswers);
  const [status, setStatus] = useState({ loading: false, err: "" });
  const [masterCode, setMasterCode] = useState("");
  // Per-field validation errors for the 4 numeric inputs (age, height_cm, weight_kg, sleep_hours_avg)
  const [errors, setErrors] = useState({ age: "", height_cm: "", weight_kg: "", sleep_hours_avg: "" });
  const setFieldError = (k) => (msg) => setErrors((prev) => ({ ...prev, [k]: msg }));

  // ───── State persistence (localStorage) ─────────────────────────────────
  // Rehydrate any in-progress draft on mount so navigating back from Stripe
  // (or hitting the back button) never loses the client's answers.
  const [draftRestored, setDraftRestored] = useState(false);
  // Skip the FIRST write-effect tick on mount — otherwise it would persist
  // the (empty) defaults before the rehydrate's setState commits, racing the
  // saved data out of localStorage.
  const skipFirstPersist = useRef(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          if (parsed.identity && typeof parsed.identity === "object") setIdentity((prev) => ({ ...prev, ...parsed.identity }));
          if (parsed.answers && typeof parsed.answers === "object") setAnswers((prev) => ({ ...prev, ...parsed.answers }));
          if (typeof parsed.engagement === "boolean") setEngagement(parsed.engagement);
          if (typeof parsed.cgvAccepted === "boolean") setCgvAccepted(parsed.cgvAccepted);
          if (typeof parsed.masterCode === "string") setMasterCode(parsed.masterCode);
          setDraftRestored(true);
        }
      }
    } catch (e) {
      // Corrupted draft → discard silently
      try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (_e) { /* ignore */ }
    }
  }, []);

  // Persist on every change (debounced via React batching is enough — the writes are tiny)
  useEffect(() => {
    if (skipFirstPersist.current) {
      skipFirstPersist.current = false;
      return;
    }
    try {
      localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify({ identity, answers, engagement, cgvAccepted, masterCode }),
      );
    } catch (e) {
      // Quota exceeded or private mode → silently ignore (UX not blocked)
    }
  }, [identity, answers, engagement, cgvAccepted, masterCode]);

  const resetDraft = () => {
    if (!window.confirm(isFr
      ? "Effacer toutes les réponses enregistrées et repartir de zéro ?"
      : "Erase all saved answers and start over?")) return;
    try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (_e) { /* ignore */ }
    setIdentity({
      first_name: "", last_name: "", age: "", email: "",
      height_cm: "", weight_kg: "", blood_group: "",
      medical_followup: false, medical_followup_detail: "",
    });
    setAnswers(initialAnswers);
    setEngagement(false);
    setCgvAccepted(false);
    setMasterCode("");
    setErrors({ age: "", height_cm: "", weight_kg: "", sleep_hours_avg: "" });
    setDraftRestored(false);
  };

  const set = (k) => (v) => setAnswers((a) => ({ ...a, [k]: v }));

  const toggleArrayValue = (key, val) =>
    setAnswers((a) => ({
      ...a,
      [key]: a[key].includes(val)
        ? a[key].filter((x) => x !== val)
        : [...a[key], val],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!engagement) {
      setStatus({
        loading: false,
        err: isFr ? "Veuillez valider l'engagement." : "Please validate the engagement.",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!cgvAccepted) {
      setStatus({
        loading: false,
        err: isFr
          ? "Veuillez accepter les Mentions Légales et CGV."
          : "Please accept the Legal Notice & T&Cs.",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Block submission if any numeric field is in error
    const firstErr = Object.entries(errors).find(([, v]) => v);
    if (firstErr) {
      setStatus({
        loading: false,
        err: isFr
          ? "Veuillez corriger les champs en rouge avant de continuer."
          : "Please fix the fields in red before continuing.",
      });
      const node = document.querySelector(`[data-testid="bilan-${firstErr[0].replace("_", "-")}"]`);
      if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setStatus({ loading: true, err: "" });
    try {
      const r = await axios.post(`${API}/bilan/checkout`, {
        engagement,
        first_name: identity.first_name,
        last_name: identity.last_name,
        age: identity.age ? parseInt(identity.age) : null,
        email: identity.email,
        height_cm: identity.height_cm ? parseFloat(identity.height_cm) : null,
        weight_kg: identity.weight_kg ? parseFloat(identity.weight_kg) : null,
        blood_group: identity.blood_group || null,
        medical_followup: identity.medical_followup,
        medical_followup_detail: identity.medical_followup_detail || null,
        answers,
        locale,
        origin_url: window.location.origin,
        master_code: masterCode.trim() || null,
      });
      // SUCCESS — clear the saved draft just before navigating (Stripe redirect
      // OR direct bilan-confirmation for the master-bypass case).
      try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (_e) { /* ignore */ }
      window.location.assign(r.data.url);
    } catch (err) {
      setStatus({
        loading: false,
        err:
          err.response?.data?.detail ||
          (isFr ? "Erreur lors du paiement." : "Payment error."),
      });
    }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <PageShell
      testId="page-bilan"
      title={isFr ? "Bilan Alimentaire et de Vitalité" : "Nutritional & Vitality Assessment"}
      subtitle={
        isFr
          ? "Vous êtes l'architecte de votre propre vitalité. Ce bilan définit les bases de votre profil biologique et adapte votre Cuisine Vivante pour une transformation durable."
          : "You are the architect of your own vitality. This assessment defines the foundations of your biological profile and adapts your Living Cuisine for lasting transformation."
      }
    >
      <ConsultationModeBadge isFr={isFr} showLocal={false} testId="bilan-consultation-badge" />

      <div className="mb-8 max-w-3xl text-sm md:text-[15px] text-slate-300 leading-relaxed space-y-3" data-testid="bilan-orientation-explainer">
        <p className="font-display text-lg text-amber-glow tracking-tight">
          {isFr
            ? "Le fonctionnement du Bilan d'Orientation (19 €)"
            : "How the Orientation Assessment (€19) works"}
        </p>
        <p>
          {isFr
            ? "Ce questionnaire est un outil d'orientation pour vous donner vos premières clés d'autonomie. Il ne constitue en aucun cas un bilan médical ou une consultation thérapeutique personnalisée."
            : "This questionnaire is an orientation tool designed to give you your first keys to autonomy. It does not in any way constitute a medical assessment or a personalised therapeutic consultation."}
        </p>
        <p>
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#00F0FF] mr-1.5">
            {isFr ? "Option A :" : "Option A:"}
          </span>
          {isFr
            ? "Les résultats vous parlent et vous repartez avec les clés nécessaires pour ajuster vous-même votre assiette et vos rythmes, en toute autonomie."
            : "The results speak to you, and you leave with the keys needed to adjust your plate and your rhythms yourself, in complete autonomy."}
        </p>
        <p>
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#00F0FF] mr-1.5">
            {isFr ? "Option B :" : "Option B:"}
          </span>
          {isFr
            ? "Le bilan met en lumière des déséquilibres plus profonds qui dépassent le cadre de la simple hygiène de vie. Dans ce cas, mon rôle de conseiller s'arrête ici. Ce bilan pourra éventuellement vous servir de support d'information si vous choisissez de le présenter aux thérapeutes ou professionnels de santé de votre choix pour un accompagnement personnalisé."
            : "The assessment brings to light deeper imbalances that go beyond the scope of simple lifestyle hygiene. In that case, my role as an advisor stops here. This assessment may serve as informational support if you choose to present it to the therapists or health professionals of your choice for personalised care."}
        </p>
      </div>

      {draftRestored && (
        <div
          className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-4 py-3 text-sm text-slate-200"
          data-testid="bilan-draft-restored"
        >
          <span className="leading-relaxed">
            {isFr
              ? "✓ Vos réponses précédentes ont été restaurées automatiquement. Vous pouvez reprendre là où vous en étiez."
              : "✓ Your previous answers have been restored automatically. You can pick up where you left off."}
          </span>
          <button
            type="button"
            onClick={resetDraft}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-600/50 px-2.5 py-1 text-xs text-slate-300 hover:border-red-500/50 hover:text-red-300 transition"
            data-testid="bilan-draft-reset"
          >
            <RotateCcw size={12} />
            {isFr ? "Recommencer" : "Start over"}
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5 mb-8" data-testid="bilan-promise-banner">
        <div className="glass glow-border rounded-2xl p-6 lg:col-span-2">
          <p className="label-overline mb-3">
            {isFr ? "Ce que vous recevez sous 48h" : "What you receive within 48h"}
          </p>
          <ul className="space-y-2 text-sm text-slate-200 leading-relaxed">
            <li>— {isFr ? "Votre analyse de profil complète selon les 5 piliers (Sanguin, Ayurvéda, Naturopathie, Chronobiologie, Jeûne)." : "Your full profile analysis along the 5 pillars (Blood, Ayurveda, Naturopathy, Chronobiology, Fasting)."}</li>
            <li>— {isFr ? "Calcul des deux scores doshas dominants pour un profil combiné (ex : Vata-Pitta)." : "Calculation of the two dominant dosha scores for a combined profile (e.g., Vata-Pitta)."}</li>
            <li>— {isFr ? "9 recettes personnalisées : 3 repas × 3 niveaux (Cantine, Chef, Chef Étoilé)." : "9 personalised recipes: 3 meals × 3 levels (Cantine, Chef, Étoilé)."}</li>
            <li>— {isFr ? "Méthodologie de cuisson détaillée + liste de courses complète avec quantités précises." : "Detailed cooking methodology + full shopping list with precise quantities."}</li>
            <li>— {isFr ? "Ingrédients faciles à trouver en supermarché — aucune denrée exotique introuvable." : "Easy-to-find supermarket ingredients — no rare exotic items."}</li>
          </ul>
        </div>
        <div className="glass rounded-2xl p-6 border border-[#D4AF37]/40 flex flex-col justify-center">
          <p className="label-overline mb-2 text-[#D4AF37]">
            {isFr ? "Tarif unique" : "One-time price"}
          </p>
          <p className="font-display text-4xl text-amber-glow tracking-tight mb-2" data-testid="bilan-price">
            19 €
          </p>
          <p className="text-xs text-slate-400 leading-relaxed">
            {isFr
              ? "Bilan personnalisé + 9 recettes + méthode. Paiement sécurisé Stripe. TVA non applicable (Art. 293 B du CGI)."
              : "Personalised assessment + 9 recipes + method. Secure Stripe payment. VAT not applicable."}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="bilan-form" noValidate>
        {/* ========== ENGAGEMENT + CGV (unchanged) ========== */}
        <div className="glass rounded-2xl p-6 md:p-7 border border-[#D4AF37]/30" data-testid="bilan-engagement-block">
          <p className="label-overline mb-3 text-[#D4AF37]">
            {isFr ? "Engagement de Responsabilité" : "Responsibility Engagement"}
          </p>
          <p className="text-sm text-slate-300 leading-relaxed mb-4">
            {isFr
              ? "L'Approche FACOW360 est une méthode d'accompagnement à la vitalité globale. Je comprends que Fabrice propose un suivi de bien-être qui ne se substitue en aucun cas à un suivi de santé conventionnel. Je reconnais être pleinement responsable de mes choix d'hygiène de vie et j'accepte que ce bilan serve exclusivement à optimiser ma forme et mon harmonie générale."
              : "The FACOW360 Approach is a method of accompaniment to global vitality. I understand that Fabrice offers a well-being follow-up that does not in any way replace conventional health care. I acknowledge being fully responsible for my lifestyle choices and accept that this assessment serves exclusively to optimise my form and general harmony."}
          </p>
          <label className="inline-flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={engagement}
              onChange={(e) => setEngagement(e.target.checked)}
              className="w-4 h-4 accent-[#00F0FF]"
              data-testid="bilan-engagement-checkbox"
              required
            />
            <span className="text-sm font-mono-tech tracking-wide uppercase text-slate-100">
              {isFr ? "J'AI LU ET JE VALIDE CET ENGAGEMENT" : "I HAVE READ AND VALIDATE THIS ENGAGEMENT"}
            </span>
          </label>

          <div className="mt-4 pt-4 border-t border-slate-700/50">
            <label className="inline-flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cgvAccepted}
                onChange={(e) => setCgvAccepted(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[#00F0FF] shrink-0"
                data-testid="bilan-cgv-checkbox"
                required
              />
              <span className="text-sm font-mono-tech tracking-wide uppercase text-slate-100 leading-snug">
                {isFr ? (
                  <>
                    J&apos;AI LU ET J&apos;ACCEPTE LES{" "}
                    <a href="/mentions-legales" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline" data-testid="bilan-cgv-link">
                      MENTIONS LÉGALES ET CGV
                    </a>
                  </>
                ) : (
                  <>
                    I HAVE READ AND ACCEPT THE{" "}
                    <a href="/mentions-legales" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline" data-testid="bilan-cgv-link">
                      LEGAL NOTICE & T&CS
                    </a>
                  </>
                )}
              </span>
            </label>
          </div>
        </div>

        {/* ========== IDENTITY (with new fields: taille, poids, groupe sanguin) ========== */}
        <div className="glass glow-border rounded-2xl p-6 md:p-7 grid md:grid-cols-2 gap-4" data-testid="bilan-identity-block">
          <div className="md:col-span-2">
            <p className="label-overline mb-1">{isFr ? "Vos informations" : "Your information"}</p>
          </div>
          <Field label={isFr ? "Prénom" : "First name"}>
            <input className="input-tech" required value={identity.first_name}
              onChange={(e) => setIdentity({ ...identity, first_name: e.target.value })}
              data-testid="bilan-first-name" />
          </Field>
          <Field label={isFr ? "Nom" : "Last name"}>
            <input className="input-tech" required value={identity.last_name}
              onChange={(e) => setIdentity({ ...identity, last_name: e.target.value })}
              data-testid="bilan-last-name" />
          </Field>
          <Field label={isFr ? "Âge" : "Age"}>
            <NumericInput
              integer
              value={identity.age}
              onChange={(v) => setIdentity({ ...identity, age: v })}
              min={1}
              max={120}
              error={errors.age}
              setError={setFieldError("age")}
              testId="bilan-age"
              isFr={isFr}
            />
          </Field>
          <Field label="Email">
            <input className="input-tech" type="email" required value={identity.email}
              onChange={(e) => setIdentity({ ...identity, email: e.target.value })}
              data-testid="bilan-email" />
          </Field>
          <Field label={isFr ? "Taille (cm)" : "Height (cm)"}>
            <NumericInput
              integer
              value={identity.height_cm}
              onChange={(v) => setIdentity({ ...identity, height_cm: v })}
              min={50}
              max={250}
              error={errors.height_cm}
              setError={setFieldError("height_cm")}
              testId="bilan-height"
              isFr={isFr}
              placeholder={isFr ? "Ex : 175" : "E.g. 175"}
            />
          </Field>
          <Field label={isFr ? "Poids actuel (kg)" : "Current weight (kg)"}>
            <NumericInput
              value={identity.weight_kg}
              onChange={(v) => setIdentity({ ...identity, weight_kg: v })}
              min={20}
              max={300}
              error={errors.weight_kg}
              setError={setFieldError("weight_kg")}
              testId="bilan-weight"
              isFr={isFr}
              placeholder={isFr ? "Ex : 75 ou 75.5" : "E.g. 75 or 75.5"}
            />
          </Field>
          <div className="md:col-span-2">
            <Field label={isFr ? "Groupe sanguin" : "Blood group"}>
              <select
                className="input-tech"
                value={identity.blood_group}
                onChange={(e) => setIdentity({ ...identity, blood_group: e.target.value })}
                data-testid="bilan-blood-group"
              >
                <option value="">{isFr ? "— Sélectionner —" : "— Select —"}</option>
                <option value="O">O</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="AB">AB</option>
                <option value="unknown">{isFr ? "Je ne sais pas encore" : "I don't know yet"}</option>
              </select>
              {identity.blood_group === "unknown" && (
                <p className="mt-3 text-xs text-[#D4AF37] italic leading-relaxed border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg px-3 py-2">
                  {isFr
                    ? "Astuce FACOW360 : il existe des autotests de groupe sanguin en pharmacie ou sur internet pour environ 15–20 €. Rapide à faire chez soi en quelques minutes, et indispensable pour adapter votre Cuisine Vivante."
                    : "FACOW360 tip: blood group self-tests are available in pharmacies or online for around €15-20. Quick to do at home in a few minutes — essential to adapt your Living Cuisine."}
                </p>
              )}
            </Field>
          </div>
        </div>

        {/* ========== PARCOURS (unchanged) ========== */}
        <Card
          num={null}
          title={isFr ? "Votre parcours — Suivi & accompagnement" : "Your journey — Current follow-up"}
          subtitle={isFr ? "Êtes-vous actuellement accompagné par un professionnel pour un parcours spécifique ?" : "Are you currently accompanied by a professional for a specific journey?"}
          testId="bilan-followup-block"
        >
          <Field label={isFr ? "Suivi actuel" : "Current follow-up"}>
            <div className="flex gap-2">
              <Check
                checked={!identity.medical_followup}
                onChange={() => setIdentity({ ...identity, medical_followup: false, medical_followup_detail: "" })}
                testId="bilan-followup-no"
              >
                {isFr ? "Non" : "No"}
              </Check>
              <Check
                checked={identity.medical_followup}
                onChange={() => setIdentity({ ...identity, medical_followup: true })}
                testId="bilan-followup-yes"
              >
                {isFr ? "Oui" : "Yes"}
              </Check>
            </div>
            {identity.medical_followup && (
              <>
                <p className="text-xs text-slate-400 italic mt-3">
                  {isFr
                    ? "Mon réseau de thérapeutes référents est à votre disposition pour harmoniser mes conseils avec votre suivi actuel."
                    : "My referenced therapists' network is at your disposal to harmonise my advice with your current follow-up."}
                </p>
                <input
                  className="input-tech mt-3"
                  placeholder={isFr ? "Précisez le type de suivi (facultatif)" : "Specify the type of follow-up (optional)"}
                  value={identity.medical_followup_detail}
                  onChange={(e) => setIdentity({ ...identity, medical_followup_detail: e.target.value })}
                  data-testid="bilan-followup-detail"
                />
              </>
            )}
          </Field>
        </Card>

        {/* ========== OBJECTIF PRINCIPAL ========== */}
        <Card
          num={null}
          title={isFr ? "Ton objectif principal" : "Your main objective"}
          subtitle={isFr ? "Cochez la ou les cases qui vous correspondent" : "Tick the box(es) that match you"}
          testId="bilan-objective-block"
        >
          <div className="flex flex-wrap gap-2">
            {OBJECTIVES.map((o) => (
              <Check
                key={o.v}
                checked={answers.objective_tags.includes(o.v)}
                onChange={() => toggleArrayValue("objective_tags", o.v)}
                testId={`bilan-obj-${o.v}`}
              >
                {isFr ? o.l_fr : o.l_en}
              </Check>
            ))}
          </div>
          <Field label={isFr ? "Autre objectif (facultatif)" : "Other objective (optional)"}>
            <input
              className="input-tech"
              type="text"
              value={answers.objective_other}
              onChange={(e) => set("objective_other")(e.target.value)}
              placeholder={isFr ? "Décrivez en une phrase…" : "Describe in one sentence…"}
              data-testid="bilan-obj-other"
            />
          </Field>
        </Card>

        {/* ========== INTRO QUESTIONNAIRE ========== */}
        <div className="text-center py-4">
          <p className="label-overline mb-1 text-[#00F0FF]">
            {isFr ? "Le Questionnaire FACOW360" : "The FACOW360 Questionnaire"}
          </p>
          <p className="text-sm text-slate-400 italic">
            {isFr ? "En 5 étapes — environ 5 minutes" : "5 steps — about 5 minutes"}
          </p>
        </div>

        {/* ========== ÉTAPE 1 — RYTHME QUOTIDIEN ========== */}
        <Card num="1" kind="etape" title={isFr ? "Ton rythme quotidien" : "Your daily rhythm"} testId="bilan-step-1">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={isFr ? "1. Heure de réveil habituelle" : "1. Usual wake-up time"}>
              <input className="input-tech" type="time" value={answers.wake_time}
                onChange={(e) => set("wake_time")(e.target.value)} data-testid="bilan-q1-wake" />
            </Field>
            <Field label={isFr ? "2. Heure du dernier repas" : "2. Last meal time"}>
              <input className="input-tech" type="time" value={answers.last_meal_time}
                onChange={(e) => set("last_meal_time")(e.target.value)} data-testid="bilan-q2-lastmeal" />
            </Field>
            <Field label={isFr ? "3. Heures de sommeil moyennes par nuit" : "3. Average sleep hours per night"}>
              <NumericInput
                value={answers.sleep_hours_avg}
                onChange={(v) => set("sleep_hours_avg")(v)}
                min={0}
                max={14}
                error={errors.sleep_hours_avg}
                setError={setFieldError("sleep_hours_avg")}
                testId="bilan-q3-sleep"
                isFr={isFr}
                placeholder={isFr ? "Ex : 7 ou 7.5" : "E.g. 7 or 7.5"}
              />
            </Field>
            <Field
              label={isFr ? "4. Réveils nocturnes (toilettes)" : "4. Night-time wake-ups (bathroom)"}
              hint={isFr ? "Combien de fois en moyenne par nuit ?" : "How many times on average per night?"}
            >
              <input className="input-tech" type="text" value={answers.night_wake_ups}
                onChange={(e) => set("night_wake_ups")(e.target.value)}
                placeholder={isFr ? "ex. 1 à 2 fois, jamais…" : "e.g. 1-2 times, never…"}
                data-testid="bilan-q4-nightwake" />
            </Field>
          </div>
          <Field
            label={isFr ? "5. Niveau d'énergie moyen dans la journée" : "5. Average daytime energy level"}
            hint={isFr ? "Sur une échelle de 1 (très bas) à 10 (excellent)" : "On a scale of 1 (very low) to 10 (excellent)"}
          >
            <ScaleSelector value={answers.daily_energy_level} onChange={set("daily_energy_level")} testIdPrefix="bilan-q5-energy" />
          </Field>
        </Card>

        {/* ========== ÉTAPE 2 — DIGESTION & BIEN-ÊTRE ========== */}
        <Card num="2" kind="etape" title={isFr ? "Digestion et bien-être" : "Digestion and well-being"} testId="bilan-step-2">
          <Field
            label={isFr ? "6. Évaluation de votre digestion globale" : "6. Overall digestion rating"}
            hint={isFr ? "Sur une échelle de 1 (très difficile) à 10 (parfaite)" : "On a scale of 1 (very difficult) to 10 (perfect)"}
          >
            <ScaleSelector value={answers.digestion_score} onChange={set("digestion_score")} testIdPrefix="bilan-q6-dig" />
          </Field>

          <Field label={isFr ? "7. Ballonnements, gaz, lourdeur ou fatigue après les repas" : "7. Bloating, gas, heaviness or fatigue after meals"}>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {[
                  { v: "souvent", l_fr: "Souvent", l_en: "Often" },
                  { v: "parfois", l_fr: "Parfois", l_en: "Sometimes" },
                  { v: "rarement", l_fr: "Rarement", l_en: "Rarely" },
                ].map((o) => (
                  <Radio key={o.v} name="bloat_freq" value={o.v}
                    current={answers.bloating_frequency} onChange={set("bloating_frequency")}
                    testId={`bilan-q7-freq-${o.v}`}>
                    {isFr ? o.l_fr : o.l_en}
                  </Radio>
                ))}
              </div>
              {answers.bloating_frequency && answers.bloating_frequency !== "rarement" && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-slate-500 self-center mr-2">
                    {isFr ? "Plutôt le :" : "Rather at:"}
                  </span>
                  {[
                    { v: "midi", l_fr: "Midi", l_en: "Midday" },
                    { v: "soir", l_fr: "Soir", l_en: "Evening" },
                  ].map((o) => (
                    <Radio key={o.v} name="bloat_time" value={o.v}
                      current={answers.bloating_timing} onChange={set("bloating_timing")}
                      testId={`bilan-q7-time-${o.v}`}>
                      {isFr ? o.l_fr : o.l_en}
                    </Radio>
                  ))}
                </div>
              )}
            </div>
          </Field>

          <Field label={isFr ? "8. Vos selles en général" : "8. Your stools in general"}>
            <input className="input-tech" type="text" value={answers.stool_description}
              onChange={(e) => set("stool_description")(e.target.value)}
              placeholder={isFr ? "ex. régulières le matin, fragmentées, molles…" : "e.g. regular in the morning, fragmented, soft…"}
              data-testid="bilan-q8-stool" />
          </Field>

          <Field label={isFr ? "9. Inconforts cutanés, cheveux ou ongles" : "9. Skin, hair or nail discomfort"}>
            <input className="input-tech" type="text" value={answers.skin_hair_nails}
              onChange={(e) => set("skin_hair_nails")(e.target.value)}
              placeholder={isFr ? "ex. ongles striés ou cassants, eczéma…" : "e.g. ridged/brittle nails, eczema…"}
              data-testid="bilan-q9-skin" />
          </Field>

          <Field
            label={isFr ? "10. Médicaments ou compléments alimentaires réguliers" : "10. Regular medication or supplements"}
            hint={isFr ? "Précisez le type sans détailler les dosages" : "Specify the type without detailing dosages"}
          >
            <input className="input-tech" type="text" value={answers.medications_supplements}
              onChange={(e) => set("medications_supplements")(e.target.value)}
              placeholder={isFr ? "ex. magnésium, oméga 3, aucun…" : "e.g. magnesium, omega 3, none…"}
              data-testid="bilan-q10-meds" />
          </Field>
        </Card>

        {/* ========== ÉTAPE 3 — ALIMENTATION ACTUELLE ========== */}
        <Card num="3" kind="etape" title={isFr ? "Ton alimentation actuelle" : "Your current diet"} testId="bilan-step-3">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={isFr ? "11. Nombre de repas par jour" : "11. Meals per day"}>
              <input className="input-tech" type="text" value={answers.meals_per_day}
                onChange={(e) => set("meals_per_day")(e.target.value)}
                placeholder={isFr ? "ex. 2, 3, grignotage…" : "e.g. 2, 3, snacking…"}
                data-testid="bilan-q11-meals" />
            </Field>
            <Field label={isFr ? "12. Viande rouge / poulet / poisson / œufs" : "12. Red meat / chicken / fish / eggs"}>
              <input className="input-tech" type="text" value={answers.meat_fish_consumption}
                onChange={(e) => set("meat_fish_consumption")(e.target.value)}
                placeholder={isFr ? "Quoi et à quelle fréquence ?" : "What and how often?"}
                data-testid="bilan-q12-meat" />
            </Field>
            <Field label={isFr ? "13. Produits laitiers / gluten / sucre" : "13. Dairy / gluten / sugar"}>
              <input className="input-tech" type="text" value={answers.dairy_gluten_sugar}
                onChange={(e) => set("dairy_gluten_sugar")(e.target.value)}
                placeholder={isFr ? "Quoi et à quelle fréquence ?" : "What and how often?"}
                data-testid="bilan-q13-dairy" />
            </Field>
            <Field label={isFr ? "14. Légumineuses" : "14. Pulses / legumes"}>
              <input className="input-tech" type="text" value={answers.legumes}
                onChange={(e) => set("legumes")(e.target.value)}
                placeholder={isFr ? "Lentilles, pois chiches… et fréquence" : "Lentils, chickpeas… and frequency"}
                data-testid="bilan-q14-legumes" />
            </Field>
          </div>

          <Field label={isFr ? "15. Votre petit-déjeuner type" : "15. Your typical breakfast"}>
            <textarea className="input-tech min-h-[80px] leading-relaxed" value={answers.typical_breakfast}
              onChange={(e) => set("typical_breakfast")(e.target.value)}
              placeholder={isFr ? "Décrivez librement…" : "Describe freely…"}
              data-testid="bilan-q15-breakfast" />
          </Field>
          <Field label={isFr ? "16. Votre déjeuner type" : "16. Your typical lunch"}>
            <textarea className="input-tech min-h-[80px] leading-relaxed" value={answers.typical_lunch}
              onChange={(e) => set("typical_lunch")(e.target.value)}
              placeholder={isFr ? "Décrivez librement…" : "Describe freely…"}
              data-testid="bilan-q16-lunch" />
          </Field>
          <Field label={isFr ? "17. Votre dîner type" : "17. Your typical dinner"}>
            <textarea className="input-tech min-h-[80px] leading-relaxed" value={answers.typical_dinner}
              onChange={(e) => set("typical_dinner")(e.target.value)}
              placeholder={isFr ? "Décrivez librement…" : "Describe freely…"}
              data-testid="bilan-q17-dinner" />
          </Field>
        </Card>

        {/* ========== ÉTAPE 4 — SENSATIONS CORPORELLES (Ayurveda + Naturopathie) ========== */}
        <Card
          num="4"
          kind="etape"
          title={isFr ? "Tes sensations corporelles" : "Your bodily sensations"}
          subtitle={isFr ? "Ayurveda & Naturopathie — pour identifier votre dosha dominant" : "Ayurveda & Naturopathy — to identify your dominant dosha"}
          testId="bilan-step-4"
        >
          <Field label={isFr ? "18. Vos aliments sont mieux digérés quand ils sont :" : "18. Your food is better digested when it is:"}>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "chauds", l_fr: "Chauds", l_en: "Warm" },
                { v: "crus", l_fr: "Crus", l_en: "Raw" },
                { v: "both", l_fr: "Les deux", l_en: "Both" },
              ].map((o) => (
                <Radio key={o.v} name="food_temp" value={o.v}
                  current={answers.food_temp_preference} onChange={set("food_temp_preference")}
                  testId={`bilan-q18-${o.v}`}>
                  {isFr ? o.l_fr : o.l_en}
                </Radio>
              ))}
            </div>
          </Field>

          <Field label={isFr ? "19. Votre température corporelle est plutôt :" : "19. Your body temperature is rather:"}>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "frileux", l_fr: "Frileux", l_en: "Cold-sensitive" },
                { v: "chaud", l_fr: "Chaud", l_en: "Warm" },
                { v: "normal", l_fr: "Normal", l_en: "Normal" },
              ].map((o) => (
                <Radio key={o.v} name="body_temp" value={o.v}
                  current={answers.body_temperature} onChange={set("body_temperature")}
                  testId={`bilan-q19-${o.v}`}>
                  {isFr ? o.l_fr : o.l_en}
                </Radio>
              ))}
            </div>
          </Field>

          <Field label={isFr ? "20. Vos émotions dominantes ces derniers temps :" : "20. Your dominant emotions recently:"}>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "calme", l_fr: "Calme", l_en: "Calm" },
                { v: "irritable", l_fr: "Irritable", l_en: "Irritable" },
                { v: "anxieux", l_fr: "Anxieux", l_en: "Anxious" },
                { v: "lent", l_fr: "Lent", l_en: "Slow" },
              ].map((o) => (
                <Radio key={o.v} name="emotions" value={o.v}
                  current={answers.dominant_emotion} onChange={set("dominant_emotion")}
                  testId={`bilan-q20-${o.v}`}>
                  {isFr ? o.l_fr : o.l_en}
                </Radio>
              ))}
            </div>
          </Field>

          <Field label={isFr ? "21. Vos préférences gustatives (plusieurs choix possibles)" : "21. Your taste preferences (multi-select)"}>
            <div className="flex flex-wrap gap-2">
              {TASTES.map((t2) => (
                <Check
                  key={t2.v}
                  checked={answers.taste_preferences.includes(t2.v)}
                  onChange={() => toggleArrayValue("taste_preferences", t2.v)}
                  testId={`bilan-q21-${t2.v}`}
                >
                  {isFr ? t2.l_fr : t2.l_en}
                </Check>
              ))}
            </div>
          </Field>
        </Card>

        {/* ========== ÉTAPE 5 — CHRONO / JEÛNE / SPORT ========== */}
        <Card num="5" kind="etape" title={isFr ? "Chronobiologie, jeûne et sport" : "Chronobiology, fasting and sport"} testId="bilan-step-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={isFr ? "22. Heure de votre plus grande faim" : "22. Time of your strongest hunger"}>
              <input className="input-tech" type="time" value={answers.peak_hunger_time}
                onChange={(e) => set("peak_hunger_time")(e.target.value)} data-testid="bilan-q22-hunger" />
            </Field>
            <Field label={isFr ? "23. Heure de votre pic d'énergie" : "23. Time of your energy peak"}>
              <input className="input-tech" type="time" value={answers.peak_energy_time}
                onChange={(e) => set("peak_energy_time")(e.target.value)} data-testid="bilan-q23-energy" />
            </Field>
          </div>
          <Field
            label={isFr ? "24. Pratique du jeûne intermittent" : "24. Intermittent fasting practice"}
            hint={isFr ? "Précisez votre fenêtre actuelle (ex. 16/8, repas 12h–20h, aucun…)" : "Specify your current window (e.g. 16/8, meals 12pm–8pm, none…)"}
          >
            <input className="input-tech" type="text" value={answers.intermittent_fasting_window}
              onChange={(e) => set("intermittent_fasting_window")(e.target.value)}
              placeholder={isFr ? "ex. 16/8 — fenêtre 12h–20h" : "e.g. 16/8 — window 12pm–8pm"}
              data-testid="bilan-q24-fasting" />
          </Field>
          <Field
            label={isFr ? "25. Activité physique" : "25. Physical activity"}
            hint={isFr ? "Type d'activité et horaire habituel" : "Activity type and usual time"}
          >
            <input className="input-tech" type="text" value={answers.physical_activity}
              onChange={(e) => set("physical_activity")(e.target.value)}
              placeholder={isFr ? "ex. marche 45 min le matin, musculation 18h…" : "e.g. 45-min morning walk, weights 6pm…"}
              data-testid="bilan-q25-activity" />
          </Field>
        </Card>

        {status.err && (
          <p className="text-sm text-red-400" data-testid="bilan-error">
            {status.err}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-4">
          <button
            type="submit"
            disabled={status.loading || !engagement || !cgvAccepted}
            className="btn-gold"
            data-testid="bilan-submit"
          >
            {status.loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {status.loading
              ? isFr ? "Redirection vers le paiement…" : "Redirecting to payment…"
              : isFr ? "Générer mon analyse FACOW360 (19 €)" : "Generate my FACOW360 analysis (€19)"}
          </button>
          {(!engagement || !cgvAccepted) && (
            <span className="text-xs text-slate-500 italic">
              {isFr ? "Validez les deux cases ci-dessus pour activer le paiement." : "Tick both boxes above to enable payment."}
            </span>
          )}
        </div>

        <details className="text-xs text-slate-500 mt-2" data-testid="bilan-master-details">
          <summary className="cursor-pointer hover:text-slate-300">
            {isFr ? "Code d'accès propriétaire (optionnel)" : "Owner access code (optional)"}
          </summary>
          <div className="mt-3 flex gap-2 max-w-md">
            <input
              type="text"
              className="input-tech text-xs"
              placeholder={isFr ? "Saisir votre code maître" : "Enter your master code"}
              value={masterCode}
              onChange={(e) => setMasterCode(e.target.value)}
              data-testid="bilan-master-input"
            />
          </div>
          <p className="text-[10px] text-slate-600 mt-2 italic">
            {isFr
              ? "Réservé au propriétaire. Génération immédiate du bilan sans paiement."
              : "Owner only. Immediate bilan generation without payment."}
          </p>
        </details>

        <p className="text-xs text-slate-500 mt-6 italic leading-relaxed border-t border-slate-700/50 pt-4">
          {isFr
            ? "Mon conseil culinaire est basé sur mon expérience professionnelle de cuisinier et ne constitue pas un bilan diététique ou médical. Cette approche de vitalité globale met en œuvre des moyens sensibles mais ne peut garantir de résultats sur des pathologies physiques ou psychologiques qui relèvent de la médecine."
            : "My culinary guidance is based on my professional cooking experience and does not constitute a dietary or medical assessment. This holistic vitality approach uses sensitive means but cannot guarantee results on physical or psychological conditions falling within the scope of medicine."}
        </p>
        <p className="text-xs text-slate-500 mt-3">{t("disclaimer")}</p>
      </form>
    </PageShell>
  );
}
